///
/// EarthBrowser
///
/// Created by Matt Giger on 12/3/2025
/// Copyright (c) 2025 Geodata Labs
///

import { spherical } from "@/earthbrowser/utils/spherical"
import { GestureState } from "@stovokor/gestures/GestureRecognizer"
import { PanGestureRecognizer, type PanGestureConfig, type PanGestureEventData } from "@stovokor/gestures/PanGestureRecognizer"
import { trig } from "@stovokor/math"
import { type StovokorContext } from "@stovokor/StovokorContext"
import type { EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import type { EntityCameraState } from "./EntityCameraState"

const cMovementSpringDamping = 30

export interface EntityPanConfig extends PanGestureConfig {
	/** Sensitivity multiplier for rotation (default: 0.001 * π) */
	sensitivity?: number
	/** Minimum phi angle to prevent flipping (default: 0.001) */
	minPhi?: number
	/** Maximum phi angle to prevent flipping (default: π - 0.001) */
	maxPhi?: number
}

/**
 * EntityPanHandler - Pan gesture recognizer for orbiting around a target entity
 *
 * Extends PanGestureRecognizer to handle single-finger pan gestures that
 * rotate the camera around a target (e.g., satellite), allowing the user
 * to view it from different angles.
 *
 * Works with EntityCameraState which manages the spring animation and
 * camera positioning relative to the moving target.
 */
export class EntityPanHandler extends PanGestureRecognizer {
	private context: StovokorContext
	private cameraState: EntityCameraState
	private sensitivity: number
	private minPhi: number
	private maxPhi: number

	/** Anchor spherical position when pan started */
	private panAnchor = new spherical()
	/** Anchor mouse position when pan started */
	private startX = 0
	private startY = 0

	constructor(context: StovokorContext, cameraState: EntityCameraState, config: EntityPanConfig = {}) {
		super({
			minimumMovement: 5,
			minimumTouches: 1,
			maximumTouches: 1,
			...config
		})
		this.context = context
		this.cameraState = cameraState
		// Sensitivity: 0.005 * π means 100 pixels → ~90 degrees rotation
		this.sensitivity = config.sensitivity ?? 0.005 * trig.pi
		this.minPhi = config.minPhi ?? 0.001
		this.maxPhi = config.maxPhi ?? trig.pi - 0.001
		// Higher priority than EarthPanHandler (50) so entity pan wins when focused
		this.priority = 20

		this.onGesture(this.panUpdate.bind(this))
	}

	private panUpdate(data: PanGestureEventData): void {
		switch (data.state) {
			case GestureState.Began:
				this.startPan(data)
				break
			case GestureState.Changed:
				this.updatePan(data)
				break
			case GestureState.Ended:
				this.endPan(data)
				break
		}
	}

	/**
	 * Override to capture pan anchor state when gesture begins
	 */
	private startPan(data: PanGestureEventData): void {
		// this.context.logger.log("EntityPanHandler begin")

		if (!this.owner) return

		// If owner is not currently focused, animate to it instead of panning
		if (!this.context.focusManager.isFocused(this.owner)) {
			// Restricted contexts (embeds) limit which entities a gesture may focus
			if (!(this.context as EarthBrowserContext).canFocusEntity(this.owner)) {
				this.cancel()
				return
			}
			// Request focus with animated transition (uses owner's focusTransition config)
			this.context.focusManager.requestFocus(this.owner)
			// Cancel this pan gesture - user can pan again after transition
			this.cancel()
			return
		}

		// Owner is focused - proceed with panning
		this.context.focusManager.requestFocus(this.owner, {
			preserveCurrentState: true
		})

		this.cameraState.syncFromCamera()

		// Capture anchor state before calling super
		const current = this.cameraState.getCurrentParams()
		this.panAnchor = current.spherical

		// Use the gesture's start position
		this.startX = data.x - data.translationX
		this.startY = data.y - data.translationY

		// Apply the initial translation
		this.applyPan(data)
	}

	/**
	 * Override to apply entity-specific pan logic
	 */
	private updatePan(data: PanGestureEventData): void {
		// this.context.logger.log("EntityPanHandler change")
		this.applyPan(data)
	}

	/**
	 * Override to finalize when gesture ends
	 */
	private endPan(_data: PanGestureEventData): void {
		// this.context.logger.log("EntityPanHandler end")
	}

	/**
	 * Apply pan translation as spherical coordinate rotation
	 */
	private applyPan(data: PanGestureEventData): void {
		// Calculate rotation deltas from mouse movement
		const deltaTheta = (data.x - this.startX) * this.sensitivity
		const deltaPhi = (data.y - this.startY) * this.sensitivity

		// Calculate new theta and phi from the anchor
		const theta = this.panAnchor.theta - deltaTheta

		// Constrain phi to valid range and camera state's maxPhi
		const constrainedMaxPhi = Math.min(this.maxPhi, this.cameraState.maxPhi())
		const phi = Math.max(this.minPhi, Math.min(constrainedMaxPhi, this.panAnchor.phi - deltaPhi))

		// Get current for shortest-path wrapping
		const current = this.cameraState.getCurrentParams()

		// Create target and wrap to closest to prevent discontinuities
		const targetSpherical = new spherical(current.radius, theta, phi)
		const closest = current.spherical.wrappedToClosest(targetSpherical)

		// Animate to new position
		const newParams = this.cameraState.createParams(closest.radius, closest.theta, closest.phi)
		this.cameraState.animateTo(newParams, cMovementSpringDamping)
	}

	override reset(): void {
		super.reset()
		this.panAnchor = new spherical()
		this.startX = 0
		this.startY = 0
	}
}
